/**
 * Temporary client-side storage for the pet photo File/Blob between funnel
 * steps. We deliberately do NOT put the image (or a base64 copy of it) into
 * sessionStorage/localStorage: those are synchronous, string-only and have a
 * ~5MB total quota, so a single photo could blow the whole funnel state.
 *
 * IndexedDB has no such practical size limit and stores real Blob/File
 * objects natively (structured clone), which is the correct browser-only
 * mechanism for this until a backend/upload endpoint exists. Only small
 * metadata (name, size, type) goes into the regular funnel state
 * (funnel-state.js) — the binary lives here.
 */
window.PfFunnelPhotoDB = (() => {
  const DB_NAME = 'pf_funnel_db';
  const STORE_NAME = 'photos';
  const KEY = 'petPhoto';

  function openDb() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB unavailable'));
        return;
      }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async function savePhoto(file) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(file, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async function getPhoto() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(KEY);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async function clearPhoto() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  return { savePhoto, getPhoto, clearPhoto };
})();
